import React, { FormEvent, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSearchEngine } from "../hooks";
import { ActiveSearchFilters } from "./ActiveSearchFilters";
import { useAppDispatch, useAppSelector } from "../store/store.hooks";
import {
  clearActiveFilters,
  selectIsLoading,
  selectSearchResult,
} from "../store/shared.reducer";
import { Facet } from "../shared.models";

interface Props {
isIntra: boolean;
}
export const SearchHeader: React.VFC<Props> = ({isIntra}) => {

  /*********************************
   * Variables
   *********************************/
  const isLoading = useAppSelector(selectIsLoading);
  const searchResult = useAppSelector(selectSearchResult);
  const dispatch = useAppDispatch();
  const searchField = useRef<HTMLInputElement>(null);
  const scopeSelect = useRef<HTMLSelectElement>(null);
  const pathName =
    typeof window !== "undefined" ? window.location.pathname : ""; // En tillfällig fix. Sökningen kommer inte att fungera utan javascript, eftersom vi behöver sökvägen till sidan.
  const [doSearch] = useSearchEngine(pathName + "/FilterStyrandeDokument");

  const [searchParams] = useSearchParams();
  const queryString = searchResult?.query.queryString;
  const searchFieldDefaultValue = queryString === "*" ? "" : queryString;
  const searchResultQueryForvaltning = new URLSearchParams(
    searchResult.query.parameters
  ).get("forvaltning");

  const scopeOptions: Facet[] = [];
  searchResult.orgFacets.forEach((facet) => {
    const query =
      new URLSearchParams(facet.query).get("forvaltning") ||
      searchResultQueryForvaltning ||
      "";
    const option = { ...facet, query };
    scopeOptions.push(option);
  });

  /*********************************
   * Functions
   *********************************/
  function onSearchSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Delete potential offset to reset pagination to page 1
    searchParams.delete("offset");
    searchParams.set("q", searchField.current.value || "*");

    // If we change scope, cleanup some params
    const scope = scopeSelect.current.value;
    if (scope !== searchResultQueryForvaltning) {
      searchParams.set("forvaltning", scope);
      searchParams.delete("org_path");
      dispatch(clearActiveFilters());
    }

    doSearch(searchParams);
  }

  const goToSearchResultList = () => {
    const searchResultList = document.getElementById("searchResultList");
    searchResultList.focus();
  };

  /*********************************
   * Render
   *********************************/
  return (
    <section
      id="searchHeader"
      aria-label="Sektion med sökfält, aktiva filter och sökningens avgränsning"
      className="vgr-searchservice__head"
    >
        <h1 id="h1id">Hitta styrande dokument</h1>
      <form method="get" action="" onSubmit={(e) => onSearchSubmit(e)}>
        <div className="search-header">
          <label className="sr-only search-header__label" htmlFor="main-search-input">
            <span className="search-header__label-text visually-hidden">
            Ange sökord</span>
          </label>
          <div className="search-header__input-group g-col-12 search-header__search-box-button-container search-control-block__search-box-button-container">
          <input
            name="q"
            id="main-search-input"
            type="search"
            placeholder="Vad letar du efter"
            autoComplete="off"
            defaultValue={searchFieldDefaultValue}
            ref={searchField}
            disabled={isLoading}
            className="search-header__input search__field "
          />
          <div className="search-header__select-wrapper">
          <label className="scope-select" >
           
            <select
                ref={scopeSelect}
                id="scope-select"
                name={"forvaltning"}
                disabled={isLoading}
                className="search-header__select"
                defaultValue={scopeOptions.find((x) => x.selected)?.query}
              >
                {scopeOptions.map((x) => {
                  return (
                    <option
                      key={`scope_option_${x.displayName}`}
                      value={x.query}
                    >
                      {x.displayName}
                    </option>
                  );
                })}
              </select>
              {isIntra ? (<></>) : (
                <> {chevron}</>
              )}
          </label>
          </div>
          <button
              onClick={goToSearchResultList}
              type="button"
              className="hits-shortcut sr-only search-header__skip-link search-control-block__skip-link"
            >
              Gå direkt till träfflistan
            </button>
            <button className="search-header__button search__button" disabled={isLoading} type="submit">
              {isIntra ? (<>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <g clipPath="url(#clip0_10284_7287)">
                        <path d="M13.0007 6.5C13.0007 7.93437 12.5351 9.25937 11.7507 10.3344L15.7071 14.2937C16.0978 14.6844 16.0978 15.3188 15.7071 15.7094C15.3165 16.1 14.6821 16.1 14.2914 15.7094L10.335 11.75C9.25989 12.5375 7.93482 13 6.50036 13C2.90954 13 0 10.0906 0 6.5C0 2.90937 2.90954 0 6.50036 0C10.0912 0 13.0007 2.90937 13.0007 6.5ZM6.50036 11C7.09134 11 7.67654 10.8836 8.22253 10.6575C8.76853 10.4313 9.26463 10.0998 9.68252 9.68198C10.1004 9.26412 10.4319 8.76804 10.6581 8.22207C10.8842 7.67611 11.0006 7.09095 11.0006 6.5C11.0006 5.90905 10.8842 5.32389 10.6581 4.77792C10.4319 4.23196 10.1004 3.73588 9.68252 3.31802C9.26463 2.90016 8.76853 2.56869 8.22253 2.34254C7.67654 2.1164 7.09134 2 6.50036 2C5.90938 2 5.32419 2.1164 4.77819 2.34254C4.2322 2.56869 3.73609 2.90016 3.3182 3.31802C2.90032 3.73588 2.56883 4.23196 2.34267 4.77792C2.11651 5.32389 2.00011 5.90905 2.00011 6.5C2.00011 7.09095 2.11651 7.67611 2.34267 8.22207C2.56883 8.76804 2.90032 9.26412 3.3182 9.68198C3.73609 10.0998 4.2322 10.4313 4.77819 10.6575C5.32419 10.8836 5.90938 11 6.50036 11Z" fill="white"></path>
                    </g>
                    <defs>
                        <clipPath id="clip0_10284_7287">
                            <rect width="16" height="16" fill="white"></rect>
                        </clipPath>
                    </defs>
                </svg>
              </>) : (
             {searchIcon}
              )}
              Sök
            </button>
          {/* <div className="select-btn-wrapper">
            <div className="select-wrapper">

             
            </div>
       
       
          </div> */}
          </div>
        </div>
        <ActiveSearchFilters />
      </form>
    </section>
  );
};

const chevron = (
  <svg aria-hidden={true} style={{ width: "16px", height: "16px" }}>
    <use xlinkHref="#streamline-arrow"></use>
  </svg>
);

const searchIcon = (
  <svg
    width="25"
    height="25"
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="none"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.3133 18.8028C19.4081 17.0625 21.3168 12.3323 19.5766 8.23759C17.8363 4.14283 13.1061 2.23411 9.01138 3.97434C4.91662 5.71457 3.0079 10.4448 4.74813 14.5395C6.48836 18.6343 11.2186 20.543 15.3133 18.8028Z"
      stroke="white"
      strokeOpacity="0.99"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.8584 17.0844L24.1064 23.3333"
      stroke="white"
      strokeOpacity="0.99"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
